import type {
  BuyerSequenceFieldRowDto,
  BuyerSequenceStepKey,
  BuyerSequenceWordingRefDto,
  LogBuyerSequenceRequest,
} from "@/api/buyerSequence";

/**
 * What COPY EMAIL puts on the clipboard, and what LOG records as the email that
 * went out: the rendered body, then — only when "Include my signature" is
 * ticked — a blank line and the rendered signature.
 *
 * 🔴 NO PHOENIX ROLE STATEMENT AND NO PLATFORM FOOTER, BY OWNER RULING (Q4/L9).
 * This text is pasted into the specialist's own mail client and sent as their
 * own correspondence, so nothing Phoenix-branded is appended here.
 *
 * That is a deliberate CONTRAST with `BrokerIntroEmailService.CopyText`, which
 * appends `PhoenixRoleStatement.AboutPhoenix` to the broker intro a user copies
 * — its reasoning is that no mail client adds a regulated role statement, so a
 * copy without it would reach a listing broker undisclosed. The owner ruled the
 * other way for buyer outreach. Do not "harmonise" the two: they answer the
 * same question differently on purpose, and the ruling that separates them is
 * the owner's, not a tidy-up's.
 *
 * Pure so a test can pin the exact bytes, and so LOG and COPY cannot assemble
 * the email two different ways.
 */
export function assembleCopiedEmail(body: string, signature: string | null): string {
  return signature === null ? body : `${body}\n\n${signature}`;
}

/** The LOG button's label — named by what is ticked (L6). */
export function logButtonLabel(textTicked: boolean, emailTicked: boolean): string {
  if (textTicked && emailTicked) return "Log text + email as sent";
  if (textTicked) return "Log text as sent";
  if (emailTicked) return "Log email as sent";
  return "Log as sent";
}

export type OutreachChannel = "text" | "email";

/**
 * One channel's message as it was sent: the body, the email's subject (null
 * for a text), and the wording it was written from.
 */
export interface SentMessage {
  body: string;
  subject: string | null;
  wording: BuyerSequenceWordingRefDto;
}

/**
 * What LOG sends for the ticked channels (L7: "the exact text copied, stored as
 * a snapshot").
 *
 * <p>A channel that was COPIED is logged from its copy, never from what the card
 * shows at LOG time. The card can change under a copy without the step or the
 * listing changing: "Use team default", a team publish arriving in a refetch,
 * or a record edit moving a merge field. The specialist sent what they copied,
 * so that is what the log records. A channel ticked WITHOUT a copy has nothing
 * else to go on, so it is logged from the card.</p>
 *
 * <p>One request when both channels share a wording, which is the normal case.
 * Two when their wordings differ, because a request carries one wording
 * source and version and the tab's log row records which wording each message
 * came from. Text first, then email.</p>
 */
export function planOutreachLog(
  stepKey: BuyerSequenceStepKey,
  listingId: string | null,
  ticked: Readonly<Record<OutreachChannel, boolean>>,
  sent: Readonly<Record<OutreachChannel, SentMessage | null>>,
): { channels: OutreachChannel[]; request: LogBuyerSequenceRequest }[] {
  const text = ticked.text ? sent.text : null;
  const email = ticked.email ? sent.email : null;

  const request = (t: SentMessage | null, e: SentMessage | null): LogBuyerSequenceRequest => {
    const wording = (t ?? e)!.wording;
    return {
      stepKey,
      listingId,
      textBody: t?.body ?? null,
      emailSubject: e?.subject ?? null,
      emailBody: e?.body ?? null,
      wordingSource: wording.source,
      wordingVersion: wording.version,
    };
  };

  if (text && email) {
    const same =
      text.wording.source === email.wording.source && text.wording.version === email.wording.version;
    return same
      ? [{ channels: ["text", "email"], request: request(text, email) }]
      : [
          { channels: ["text"], request: request(text, null) },
          { channels: ["email"], request: request(null, email) },
        ];
  }
  if (text) return [{ channels: ["text"], request: request(text, null) }];
  if (email) return [{ channels: ["email"], request: request(null, email) }];
  return [];
}

/** True when a copied message no longer matches what its card shows. */
export function differsFromCard(copy: SentMessage | undefined, card: SentMessage | null): boolean {
  if (!copy || !card) return false;
  return copy.body !== card.body || copy.subject !== card.subject;
}

/**
 * The clipboard, with the failure reported rather than swallowed. The API is
 * unavailable over plain HTTP and in some embedded browsers; the text is on
 * screen, so the caller tells the reader to select it by hand.
 */
export async function writeClipboard(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * The three fields a message cannot go out without (G11) — the specialist's own
 * name, email and phone, all edited on their profile rather than on this buyer.
 */
const PROFILE_FIELDS: Record<string, string> = {
  MyFirstName: "first name",
  MyPhone: "phone",
  MyEmail: "email",
};

/** The profile page where a specialist fills in the fields above. */
export const PROFILE_EDIT_PATH = "/account/profile/edit";

export interface MissingRequiredSummary {
  /** Nouns for "Add your … to your profile", e.g. `"phone/email"`; null when none. */
  profileNouns: string | null;
  /** Any other required field that is missing, by its label. */
  otherLabels: string[];
}

/**
 * The "Pulled from this record" rows for what is on screen (L3): the fields
 * the rendered blocks reached, and nothing else. A row for a field the step's
 * wording never uses is a row the specialist cannot find in either message.
 *
 * <p>Order is the rows' own (the field registry's), not first use, so a field
 * keeps its place as the specialist moves between steps. The signature counts
 * only while it is on the email, because only then does the message use it.
 * No message on screen means no rows.</p>
 */
export function fieldsInUse(
  rows: readonly BuyerSequenceFieldRowDto[],
  blocks: readonly ({ usedFields: readonly string[] } | null)[],
): BuyerSequenceFieldRowDto[] {
  const used = new Set(blocks.flatMap((b) => (b ? b.usedFields : [])));
  return rows.filter((r) => used.has(r.field));
}

/**
 * Splits a block's `missingRequiredFields` into the profile fields (one
 * sentence with a door to the profile) and anything else (named by the label
 * the Pulled-from card uses). Order follows `PROFILE_FIELDS`, not the wire, so
 * the sentence reads the same whichever block reported it.
 */
export function summariseMissingRequired(
  missing: readonly string[],
  rows: readonly BuyerSequenceFieldRowDto[],
): MissingRequiredSummary {
  const set = new Set(missing);
  const nouns = Object.entries(PROFILE_FIELDS)
    .filter(([field]) => set.has(field))
    .map(([, noun]) => noun);
  const otherLabels = [...set]
    .filter((field) => !(field in PROFILE_FIELDS))
    .map((field) => rows.find((r) => r.field === field)?.label ?? field);
  return {
    profileNouns: nouns.length > 0 ? nouns.join("/") : null,
    otherLabels,
  };
}
